/**
 * Logger module for asynchronous logging to Pub/Sub via its REST API.
 */

export interface LogEntry {
  timestamp: string
  severity: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR'
  userId?: string
  action: string
  additionalInfo?: Record<string, any>
}

// Global variables for configuration
let pubsubAccessToken: string | null = null
let pubsubPublishUrl: string | null = null

/**
 * Configures the logger with the environment-aware topic name and access token.
 * This should be called by the main app after determining the environment.
 */
export function configurePubsubLogger(environment: string, accessToken: string): void {
  // Environment should match infrastructure naming: {workload}-{workspace}-hitl-logger
  const topicName = `${environment}-hitl-logger`
  pubsubPublishUrl = `https://pubsub.googleapis.com/v1/projects/${process.env.PROJECT_ID}/topics/${topicName}:publish`
  pubsubAccessToken = accessToken
  console.log(`HITL Logger configured for environment: ${environment}`)
  console.log(`HITL Logger topic name: ${topicName}`)
  console.log(`HITL Logger URL: ${pubsubPublishUrl}`)
  console.log(`HITL Logger client prefix: ${process.env.CLIENT_PREFIX}`)
}

/**
 * Sets the access token to be used when publishing log entries.
 * @deprecated Use configurePubsubLogger() instead for environment-aware setup
 */
export function setPubsubAccessToken(token: string): void {
  pubsubAccessToken = token
  // Fallback to legacy topic name if no URL is configured yet
  if (!pubsubPublishUrl) {
    pubsubPublishUrl = `https://pubsub.googleapis.com/v1/projects/${process.env.PROJECT_ID}/topics/hitl_logger:publish`
    console.warn('Using legacy hitl_logger topic name. Consider using configurePubsubLogger() for environment-aware logging.')
  }
}

let logBuffer: LogEntry[] = []
const BUFFER_TIMEOUT_MS = 5000
let bufferFlushTimer: number | null = null

/**
 * Flushes the buffered logs to Pub/Sub using the REST API.
 */
function flushLogBuffer(): void {
  if (!pubsubAccessToken || !pubsubPublishUrl) {
    console.error("Pub/Sub access token or URL not set; cannot flush logs.")
    return
  }
  if (logBuffer.length > 0) {
    const logsToSend = [...logBuffer]
    logBuffer = []
    // Construct messages array as required by Pub/Sub.
    const messages = logsToSend.map(entry => ({
      data: btoa(JSON.stringify(entry))
    }))
    fetch(pubsubPublishUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${pubsubAccessToken}`
      },
      body: JSON.stringify({ messages }),
      keepalive: true
    }).catch((error) => {
      console.error(`Failed to publish logs: ${error}`)
      // Optionally, re-buffer logsToSend if needed.
    })
  }
  if (bufferFlushTimer) {
    clearTimeout(bufferFlushTimer)
    bufferFlushTimer = null
  }
}

/**
 * Adds a log entry to the buffer and schedules a flush after a fixed delay.
 * This function is nonblocking and designed to be called on every user action.
 */
export function logAction(logEntry: LogEntry): void {
  logBuffer.push(logEntry)
  if (!bufferFlushTimer) {
    bufferFlushTimer = window.setTimeout(() => {
      flushLogBuffer()
    }, BUFFER_TIMEOUT_MS)
  }
}

/**
 * Sends a critical log entry immediately.
 */
export async function sendCriticalLog(logEntry: LogEntry): Promise<void> {
  if (!pubsubAccessToken || !pubsubPublishUrl) {
    console.error("Pub/Sub access token or URL not set; cannot send critical log.")
    return
  }
  try {
    const message = {
      data: btoa(JSON.stringify(logEntry))
    }
    await fetch(pubsubPublishUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${pubsubAccessToken}`
      },
      body: JSON.stringify({ messages: [message] }),
      keepalive: true
    })
  } catch (error) {
    console.error(`Failed to send critical log: ${error}`)
  }
}

/**
 * Console logger with environment-based log level filtering.
 * In dev/sit/ci: Shows DEBUG, INFO, WARN, ERROR
 * In prod/poc: Shows INFO, WARN, ERROR (no DEBUG)
 */
export const log = {
  /**
   * Debug level - only shown in dev/sit/ci environments
   */
  debug: (...args: any[]) => {
    const env = process.env.ENVIRONMENT?.toLowerCase() || '';
    if (env.includes('dev') || env.includes('sit') || env.includes('ci')) {
      console.debug(...args);
    }
  },

  /**
   * Info level - shown in all environments
   */
  info: (...args: any[]) => {
    console.info(...args);
  },

  /**
   * Warning level - shown in all environments
   */
  warn: (...args: any[]) => {
    console.warn(...args);
  },

  /**
   * Error level - shown in all environments
   */
  error: (...args: any[]) => {
    console.error(...args);
  }
};
